import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Eye, Layers, Menu } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ImageUpload from '../components/ImageUpload';

const PAGE_TYPES = {
  regular: { label: 'Regular', color: 'bg-gray-500' },
  expo: { label: 'Expo', color: 'bg-blue-500' },
  festival: { label: 'Festival', color: 'bg-purple-500' },
};

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await api.get('/admin/pages');
      setPages(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch pages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPage(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      await api.delete(`/admin/pages/${id}`);
      toast({ title: 'Success', description: 'Page deleted' });
      fetchPages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete page',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pages Manager</h1>
          <p className="text-gray-600 mt-2">{pages.length} pages total</p>
        </div>
        <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
          <Plus size={20} className="mr-2" />
          Create Page
        </Button>
      </div>

      {pages.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-xl text-gray-600 mb-4">No pages yet</p>
          <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
            <Plus size={20} className="mr-2" />
            Create Your First Page
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => {
            const pageType = PAGE_TYPES[page.page_type] || PAGE_TYPES.regular;
            return (
              <Card key={page.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`page-${page.slug}`}>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900 line-clamp-1">
                        {page.title}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Badge className={pageType.color}>{pageType.label}</Badge>
                        <Badge variant={page.status === 'published' ? 'default' : 'secondary'}>
                          {page.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">/{page.slug}</p>
                  </div>

                  {page.meta_description && (
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {page.meta_description}
                    </p>
                  )}

                  <div className="flex items-center flex-wrap gap-2 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/pages/${page.id}/builder`)}
                      className="flex-1"
                      data-testid={`edit-sections-${page.slug}`}
                    >
                      <Layers size={16} className="mr-2" />
                      Sections
                    </Button>
                    {page.status === 'published' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/${page.slug}`, '_blank')}
                      >
                        <Eye size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(page)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(page.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPage ? 'Edit Page' : 'Create New Page'}
            </DialogTitle>
          </DialogHeader>
          <PageEditor
            page={editingPage}
            onSave={(data) => {
              setIsDialogOpen(false);
              fetchPages();
            }}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

function PageEditor({ page, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: page?.title || '',
    slug: page?.slug || '',
    meta_title: page?.meta_title || '',
    meta_description: page?.meta_description || '',
    og_image: page?.og_image || '',
    page_type: page?.page_type || 'regular',
    menu_id: page?.menu_id || '',
    status: page?.status || 'draft',
  });
  const [menus, setMenus] = useState([]);
  const [saving, setSaving] = useState(false);
  // If editing existing page, consider slug as manually set
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(!!page?.slug);
  const { toast } = useToast();

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const response = await api.get('/menus');
      setMenus(response.data);
    } catch (error) {
      console.error('Failed to fetch menus');
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.slug) {
      toast({
        title: 'Validation Error',
        description: 'Title and slug are required',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        menu_id: formData.menu_id || null,
      };
      if (page) {
        await api.put(`/admin/pages/${page.id}`, dataToSend);
        toast({ title: 'Success', description: 'Page updated' });
      } else {
        await api.post('/admin/pages', dataToSend);
        toast({ title: 'Success', description: 'Page created' });
      }
      onSave(formData);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save page',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Allow dashes while typing but clean up invalid characters
  const sanitizeSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w-]/g, '-')  // Replace invalid chars with dash
      .replace(/--+/g, '-');     // Replace multiple dashes with single
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      // Only auto-generate slug if it hasn't been manually edited
      slug: slugManuallyEdited ? prev.slug : slugify(newTitle),
      meta_title: prev.meta_title || newTitle,
    }));
  };

  const handleSlugChange = (e) => {
    setSlugManuallyEdited(true);
    setFormData(prev => ({ ...prev, slug: sanitizeSlug(e.target.value) }));
  };

  // Clean up slug on blur (remove trailing dashes)
  const handleSlugBlur = () => {
    setFormData(prev => ({ ...prev, slug: prev.slug.replace(/^-+|-+$/g, '') }));
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Page Title *
        </label>
        <Input
          value={formData.title}
          onChange={handleTitleChange}
          placeholder="About Us"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL Slug *
        </label>
        <Input
          value={formData.slug}
          onChange={handleSlugChange}
          onBlur={handleSlugBlur}
          placeholder="about-us"
        />
        <p className="text-sm text-gray-500 mt-1">
          URL: /{formData.slug || 'your-slug'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Title (SEO)
        </label>
        <Input
          value={formData.meta_title}
          onChange={(e) =>
            setFormData(prev => ({ ...prev, meta_title: e.target.value }))
          }
          placeholder="About Us | Pulse NYC"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Description (SEO)
        </label>
        <Textarea
          value={formData.meta_description}
          onChange={(e) =>
            setFormData(prev => ({ ...prev, meta_description: e.target.value }))
          }
          placeholder="Learn more about Pulse NYC..."
          rows={3}
        />
        <p className="text-sm text-gray-500 mt-1">
          {formData.meta_description.length} / 160 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Social Share Image (og:image)
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Image shown when this page is shared on Facebook, LinkedIn, Twitter. Recommended: 1200x630px
        </p>
        <ImageUpload
          value={formData.og_image}
          onChange={(url) => setFormData(prev => ({ ...prev, og_image: url }))}
          label="Social Share Image"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Page Type
        </label>
        <Select
          value={formData.page_type}
          onValueChange={(value) =>
            setFormData(prev => ({ ...prev, page_type: value }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="regular">Regular</SelectItem>
            <SelectItem value="expo">Expo</SelectItem>
            <SelectItem value="festival">Festival</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-gray-500 mt-1">
          Page type affects the visual template used for rendering
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Navigation Menu
        </label>
        <Select
          value={formData.menu_id || 'default'}
          onValueChange={(value) =>
            setFormData(prev => ({ ...prev, menu_id: value === 'default' ? '' : value }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Use default for page type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">
              <span className="text-gray-500">Use default for page type</span>
            </SelectItem>
            {menus.map((menu) => (
              <SelectItem key={menu.id} value={menu.id}>
                <div className="flex items-center gap-2">
                  <Menu size={14} />
                  {menu.name}
                  {menu.is_default && <span className="text-xs text-yellow-600">(Global Default)</span>}
                  {menu.page_type_default && (
                    <span className="text-xs text-blue-600">({menu.page_type_default} default)</span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-gray-500 mt-1">
          Override the default menu for this specific page
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>
        <Select
          value={formData.status}
          onValueChange={(value) =>
            setFormData(prev => ({ ...prev, status: value }))
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-black hover:bg-gray-800"
        >
          {saving ? 'Saving...' : 'Save Page'}
        </Button>
      </div>
    </div>
  );
}

export default PagesManager;
